import React, { Fragment, useEffect, useRef, useState } from "react";
import { Combobox } from "@headlessui/react";
import ChevronDown from "../../Atomos/Icons/ChevronDown";
import WSGCheck from "../../Atomos/Icons/WSGCheck";
import NewTooltip from "../Tooltip/NewTooltip";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import { includesFoldedSearch } from "../../../utils/foldSearchText";
import CloseModal from "../../Atomos/Icons/EditMode/CloseModal";

type Option = {
  id?: string;
  name: string;
  selected?: any;
  disable?: boolean;
  firms?: any | null;
  offices?: any | null;
};

type DDMultipleWithChipsProps = {
  disabled?: boolean;
  title: string;
  objects: Option[];
  selectedObjst: Option[];
  seter: React.Dispatch<React.SetStateAction<Option[]>>;
  error?: boolean;
  required?: boolean;
  errorMessage?: string;
  placeholder?: string;
  tooltip?: string;
  hideCheckboxes?: boolean;
  disableWrite?: boolean;
  titleClass?: string;
  ddclass?: string;
  ddOptionsClass?: string;
  ddOptionsItemClass?: string;
  rightButton?: React.ReactNode;
  allowCustomOptions?: boolean;
  createCustomOption?: (query: string) => Option | null;
  noOptionsMessage?: string;
  onInputBlur?: () => void;
};

const areOptionsEqual = (option1: Option, option2: Option): boolean => {
  if (option1.id && option2.id && option1.id === option2.id) {
    return true;
  }
  if (option1.name && option2.name && option1.name === option2.name) {
    return true;
  }
  return false;
};

function DDMultipleWithChips(props: DDMultipleWithChipsProps) {
  const objects: Option[] = React.useMemo(
    () => [...props.objects].sort((a, b) => a.name?.localeCompare(b.name)),
    [props.objects]
  );
  const [query, setQuery] = useState("");
  const { isStaffMode } = useStaffMode();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const addCustomOptionFromQuery = React.useCallback((): boolean => {
    if (!props.allowCustomOptions) {
      return false;
    }

    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      return false;
    }

    const nextOption = props.createCustomOption
      ? props.createCustomOption(trimmedQuery)
      : {
          id: trimmedQuery,
          name: trimmedQuery,
        };

    if (!nextOption?.name?.trim()) {
      return false;
    }

    const nextSelection = [...props.selectedObjst, nextOption].filter(
      (option, index, self) =>
        index === self.findIndex((candidate) => areOptionsEqual(candidate, option))
    );

    props.seter(nextSelection);
    setQuery("");
    return true;
  }, [
    props.allowCustomOptions,
    props.createCustomOption,
    props.selectedObjst,
    props.seter,
    query,
  ]);

  const filteredAndSortedObjects =
    query === ""
      ? objects
      : objects
          .filter((object) =>
            includesFoldedSearch(
              String(object.name ?? "").replace(/\s+/g, ""),
              query.replace(/\s+/g, ""),
            ),
          )
          .sort((a, b) => {
            const nameA = a.name?.toLowerCase() || "override";
            const nameB = b.name?.toLowerCase() || "override";
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          });

  useEffect(() => {
    if (props.selectedObjst?.length === 0) {
      setQuery("");
    }
  }, [props.selectedObjst]);

  const inputRef = useRef<HTMLInputElement>(null);

  const updateSelection = React.useCallback(
    (nextSelection: Option[]) => {
      const uniqueOptions = nextSelection.filter(
        (option, index, self) =>
          index === self.findIndex((candidate) => areOptionsEqual(candidate, option))
      );
      props.seter(uniqueOptions);
      setQuery("");
    },
    [props.seter]
  );

  const renderCheckbox = (selected: boolean) => {
    if (props.hideCheckboxes) {
      return null;
    }
    return (
      <div
        className={`stroke-gray-font stroke-2 ${!selected ? "fill-neutral" :
          isStaffMode && selected ? "fill-secondary" : "fill-primary"
        } h-5 w-5 min-w-[20px] min-h-[20px]`}
      >
        <WSGCheck check={selected} />
      </div>
    );
  };

  const handleFieldMouseDown = (
    event: React.MouseEvent<HTMLDivElement>,
    open: boolean,
  ) => {
    if (props.disabled) {
      return;
    }
    if ((event.target as HTMLElement).closest("[data-ddmultiplewithchips-action]")) {
      return;
    }

    event.preventDefault();
    if (!open) {
      buttonRef.current?.click();
    }
    inputRef.current?.focus();
  };

  return (
    <>
      <div className={`${props.ddclass} flex flex-col ${props.title ? 'gap-2 md:gap-[.625rem]' : ''} w-full`}>
        {props.title && (
          <div className="flex gap-2 w-full flex-1">
            <label
              htmlFor={props.title}
              className={`${props.titleClass} text-base md:text-lg font-medium text-gray-font`}
            >
              {props.title} {props.required && <span className="text-primary"> *</span>}
            </label>
            {props.tooltip && <NewTooltip text={props.tooltip}/>}
          </div>
        )}

        <div className="relative w-full">
          <Combobox<Option>
            value={props.selectedObjst}
            multiple
            disabled={props.disabled}
          >
            {({ open }) => (
              <div className="relative">
                <div
                  className={`relative items-center flex flex-wrap gap-2 px-2 md:px-4 py-0 w-full border border-solid leading-5 ${
                    props.error 
                      ? "errorInput border-primary bg-gray-25" 
                      : props.disabled
                      ? "bg-gray-100 border-gray-700"
                      : "border-gray-25 bg-gray-25"
                  }`}
                  onMouseDown={(event) => handleFieldMouseDown(event, open)}
                >
                    {props.selectedObjst && props.selectedObjst.length > 0 && (
                      <>
                        {props.selectedObjst.map((selectedObj: Option) => (
                          <div
                            key={selectedObj?.id || selectedObj?.name}
                            className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm text-gray-font"
                          >
                            <span>{selectedObj?.name || "Override"}</span>
                            {!props.disabled && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  updateSelection(
                                    props.selectedObjst.filter(
                                      (obj) => !areOptionsEqual(obj, selectedObj)
                                    )
                                  );
                                }}
                                data-ddmultiplewithchips-action
                                className="ml-1 hover:bg-gray-200 rounded-full p-0.5 flex items-center justify-center text-gray-font"
                              >
                                <div className="w-3 h-3">
                                  <CloseModal />
                                </div>
                              </button>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                    <Combobox.Input
                      ref={inputRef}
                      value={query}
                      className={`bg-transparent text-base md:text-lg leading-5 font-normal not-italic border-none flex-1 min-w-[120px] py-[.375rem] md:py-3 ${
                        props.disabled
                          ? "text-gray-200"
                          : props.error
                          ? "text-primary focus:outline-none"
                          : "text-input-gray focus:outline-none"
                      } ${props.disableWrite && "pointer-events-none cursor-default"}`}
                      displayValue={() => ""}
                      onChange={(event) => setQuery(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && props.allowCustomOptions) {
                          event.preventDefault();
                          event.stopPropagation();
                          addCustomOptionFromQuery();
                          return;
                        }
                        if (event.key === 'Backspace' && query === '' && props.selectedObjst.length > 0) {
                          event.preventDefault();
                          const lastChip = props.selectedObjst[props.selectedObjst.length - 1];
                          updateSelection(
                            props.selectedObjst.filter(
                              (obj) => !areOptionsEqual(obj, lastChip)
                            )
                          );
                        }
                      }}
                      onBlur={() => props.onInputBlur?.()}
                      onReset={() => props.seter([])}
                      readOnly={props.disableWrite}
                      placeholder={props.selectedObjst?.length === 0 ? (props.placeholder || "Select Multiple") : ""}
                    />
                    <Combobox.Button
                      ref={buttonRef}
                      data-ddmultiplewithchips-action
                      className="absolute inset-y-0 right-0 flex items-center pr-2"
                    >
                      {!open ? (
                        <div
                          id="chevron-up"
                          className={`w-6 h-6 ${
                            props.disabled ? "text-gray-200" : "text-gray-font"
                          }`}
                        >
                          <ChevronDown />
                        </div>
                      ) : (
                        <div id="chevron-down" className="w-6 h-6 rotate-180">
                          <ChevronDown />
                        </div>
                      )}
                    </Combobox.Button>
                  </div>
                  <Combobox.Options
                    className={`absolute max-h-[400px] no-scrollbar overflow-scroll flex 
                  flex-col w-full gap-x-3 opacity-1 visible scale-[100%] top-30 transform transition-transform 
                  ease-out duration-200 transition-delay-200 z-20 bg-white shadow-lg ring-1 ring-black 
                  ring-opacity-5 focus:outline-none ${props.ddOptionsClass}`}
                  >
                    {filteredAndSortedObjects?.map((object: Option) => {
                      const isSelected = props.selectedObjst.some(
                        (selectedObj) => areOptionsEqual(selectedObj, object)
                      );
                      return (
                        <Combobox.Option
                          key={object?.id || object?.name}
                          value={object}
                          as={Fragment}
                          disabled={props.disabled}
                        >
                          {({ active }) => (
                            <li 
                              className={`flex gap-[0.625rem] !py-3 pl-4 pr-3 !lg:py-3 lg:pl-5 lg:pr-4 align-middle text-base md: leading-[19px] text-gray-font bg-white hover:bg-gray-50 cursor-pointer ${props.ddOptionsItemClass}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isSelected) {
                                  updateSelection(
                                    props.selectedObjst.filter(
                                      (obj) => !areOptionsEqual(obj, object)
                                    )
                                  );
                                } else {
                                  updateSelection([...props.selectedObjst, object]);
                                }
                              }}
                            >
                              {renderCheckbox(isSelected)}
                              <div className="first-letter:uppercase">
                                {object.name}
                              </div>
                            </li>
                          )}
                        </Combobox.Option>
                      );
                    })}
                    {!filteredAndSortedObjects?.length && (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        {props.allowCustomOptions && query.trim()
                          ? `Press Enter to add "${query.trim()}"`
                          : props.noOptionsMessage || "No results found"}
                      </div>
                    )}
                  </Combobox.Options>
                </div>
              )}
          </Combobox>
        </div>

         {props.errorMessage && <span
          className={`flex flex-wrap items-center text-base font-medium text-primary min-h-[1.5rem] ${!props.error && "invisible"}`}
        >
          {props.errorMessage}
        </span>}
      </div>
    </>
  );
}

export default DDMultipleWithChips;
export type { Option };
